/**
 * ehr#689 F7 (Jay 10-30 ruling 2, COUPLES_AND_FAMILY_NOTES_10-29-V15 groupAiBody / draftEveryone):
 * the family bar's shared "What happened in the session" box, Generate everyone and Apply to
 * <name>'s note. The group page's tools (GroupNotePage, #694) with the family rules:
 *
 * - The default generator, not the group's strict one (Jay 10-30 answer 5): no strict rules and no
 *   "Client said ..." naming, as each attendee's own Generate Notes card already drafts.
 * - Sentences naming another attendee stay in (V15 groupRoughFor keeps every sentence on a family
 *   note; QA "Fam: Generate everyone keeps sentences about the other family members").
 * - Generate everyone refreshes AI text nobody has reviewed yet (the note's npDrafted boxes) and
 *   fills empty boxes; typed, edited or accepted text is never touched (V15 "V9"). Apply to
 *   <name>'s note fills empty boxes only (V15 aiDraftCore without refresh).
 */
import type { FamilyAttendeeView } from '@ehr/shared';
import { summaryToText, type NpOptions } from './notepilot.js';
import { draftAttendee } from './groupNotes.js';
import { groupTextFor } from './groupNoteV26.js';

type Body = Record<string, unknown>;
type Attendee = Pick<FamilyAttendeeView, 'name' | 'onRoster' | 'attendance' | 'note'>;

/** Jay 10-30 answer 5: family notes keep the default generator (never `{ strict: true }`). */
export const FAMILY_DRAFT_OPTIONS: NpOptions = {};

const first = (name: string) => name.split(/\s+/)[0] ?? name;

/**
 * Why this person can't generate this attendee's note from the bar, or null when they can. Not on
 * the session or Not present (V15 skips a no-show note); with the reviewer, signed or co-signed;
 * someone else's draft (an admin or manager may write any, as on the group page).
 */
export function familySkipReason(a: Attendee, me: string | null, isManager: boolean) {
  if (!a.onRoster) return 'not on the session';
  if (a.attendance === 'Not present') return 'not present';
  const st = a.note?.status;
  if (st === 'pending') return 'with the reviewer';
  if (st === 'signed' || st === 'cosigned') return 'signed';
  if (a.note && !isManager && a.note.authorId !== me) return 'another clinician’s note';
  return null;
}

/**
 * ehr#689 F7 (V15 allDis, group #694 guards): why Generate everyone is off, or null when it can run.
 * Only the session's lead (or an admin or manager) generates everyone; never in a read-only copy
 * (the family tab lock, #693); and not when no attendee's note is open to change.
 */
export function familyGenerateBlock(o: {
  lead: boolean;
  readOnly?: boolean;
  attendees: readonly Attendee[];
  me: string | null;
  isManager: boolean;
}): string | null {
  if (o.readOnly) return 'This copy is read-only. Use Open here instead to generate notes.';
  if (!o.lead) return 'Only the session’s lead clinician can generate everyone.';
  if (!o.attendees.some((a) => !familySkipReason(a, o.me, o.isManager)))
    return 'Every attendee’s note is signed, with the reviewer or read-only.';
  return null;
}

/**
 * What one attendee's note generates from: the shared notes, then their own rough notes (the note's
 * Generate Notes card, body.sessionSummary; V15 "Only about <name>").
 */
export const familyTextFor = (shared: string, body: Body) =>
  groupTextFor(shared, [], summaryToText(String(body.sessionSummary ?? '')));

/**
 * One attendee's note from the shared notes plus their own, with the default generator.
 * `refresh` (Generate everyone) also rewrites AI text nobody has reviewed; without it (Apply to
 * <name>'s note) only empty boxes are filled.
 */
export function generateFamilyNote(
  body: Body,
  format: string,
  text: string,
  s: { durationMin: number; sessionLabel: string; refresh: boolean },
): { body: Body; drafted: number } {
  const approaches = Array.isArray(body.approaches)
    ? body.approaches.filter((x): x is string => typeof x === 'string')
    : [];
  return draftAttendee(
    body,
    format,
    text,
    { durationMin: s.durationMin, approaches, sessionLabel: s.sessionLabel },
    { options: FAMILY_DRAFT_OPTIONS, refresh: s.refresh },
  );
}

/** Generate everyone's result: how many notes it wrote, then each one it left and why. */
export function familyGeneratedMsg(n: number, skipped: { name: string; reason: string }[]) {
  const head = n
    ? `Generated ${n} note${n === 1 ? '' : 's'}. Read each one and make it theirs.`
    : 'No note was generated.';
  if (!skipped.length) return head;
  return `${head} Skipped: ${skipped.map((x) => `${first(x.name)} (${x.reason})`).join(', ')}.`;
}
